import type { HTMLAttributes } from 'react';
import { cn } from './cn';

export type SpeechBubbleLevel = 'headline-sm' | 'body-lg';

// Full class strings so Tailwind's scanner sees them.
const LEVEL: Record<SpeechBubbleLevel, string> = {
  'headline-sm': 'text-headline-sm',
  'body-lg': 'text-body-lg',
};

/**
 * The coach's speech bubble: light blue card, centred text in blue-70, and a tail pointing
 * down at the mascot. Text arrives translated from the app. `lead` is an optional bold
 * opener on its own line (the welcome's "Welcome to StrongerMemory"); with `inline` it stays
 * on the same line. The end screens read in `headline-sm`, the coach's intros in the
 * smaller `body-lg`, so the coach and the buttons fit above the fold.
 */
export function SpeechBubble({
  lead,
  inline = false,
  level = 'headline-sm',
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  lead?: string;
  inline?: boolean;
  level?: SpeechBubbleLevel;
  children: string;
}) {
  return (
    <div {...props} className={cn('flex w-full flex-col items-center', className)}>
      <p
        className={cn(
          'w-full rounded-md bg-blue-10 p-content-sm text-center font-brand text-blue-70',
          LEVEL[level],
        )}
      >
        {lead ? (
          <>
            <strong className="font-bold">{lead}</strong>
            {inline ? ' ' : <br />}
          </>
        ) : null}
        {children}
      </p>
      <span
        aria-hidden
        className="block size-0 border-x-[12px] border-t-[17px] border-x-transparent border-t-blue-10"
      />
    </div>
  );
}
